import {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  FileSearch,
  FileText,
  Filter,
  MoreHorizontal,
  Trash2,
  Upload,
} from 'lucide-react';
import { api, ErroApi } from '../../../api/cliente';
import type { AnaliseSped } from '../../../api/tipos';
import {
  Aviso,
  Botao,
  BotaoLink,
  Cartao,
  Celula,
  cx,
  Esqueleto,
  Etiqueta,
  ItemMenu,
  Menu,
  Tabela,
  Td,
  Th,
  TituloSecao,
  useConfirmar,
  useNotificar,
  Vazio,
} from '../../../componentes/ui';
import { dataHora, numero, plural } from '../../../util/formato';
import { STATUS_ANALISE, TIPO_SPED } from './rotulos';
import { Pagina, TituloPagina } from '../../../componentes/Pagina';
import { ComoCobramos } from '../../../componentes/ResumoCusto';
import css from './ListaSped.module.css';

/**
 * Mesmos tetos do backend. Não são limite de produto — são o que impede um
 * envio de encher o disco temporário do servidor. Quem tem mais que isso manda
 * um ZIP, que chega como um arquivo só e é expandido lá.
 */
const MAX_ARQUIVOS = 1000;
const LIMITE_MB = 300;

const megabytes = (bytes: number) =>
  `${(bytes / 1024 / 1024).toLocaleString('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} MB`;

export function ListaSped() {
  const navegar = useNavigate();
  const confirmar = useConfirmar();
  const notificar = useNotificar();
  const [analises, setAnalises] = useState<AnaliseSped[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [escolhidos, setEscolhidos] = useState<File[]>([]);
  const [arrastando, setArrastando] = useState(false);
  const entrada = useRef<HTMLInputElement>(null);

  const carregar = useCallback(async () => {
    try {
      setAnalises(await api<AnaliseSped[]>('/sped/analises'));
    } catch (err) {
      if (err instanceof ErroApi) setErro(err.message);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  /** Mesma validação para o seletor de arquivos e para o arrastar e soltar. */
  function aceitar(lista: File[]) {
    setErro(null);
    if (lista.length > MAX_ARQUIVOS) {
      setErro(
        `São ${numero(lista.length)} arquivos, e o envio direto aceita ${numero(MAX_ARQUIVOS)}. ` +
          'Compacte a pasta num .zip e envie o ZIP — não há limite de arquivos lá dentro.',
      );
      return;
    }
    const grande = lista.find((f) => f.size > LIMITE_MB * 1024 * 1024);
    if (grande) {
      setErro(`"${grande.name}" passa de ${LIMITE_MB} MB.`);
      return;
    }
    setEscolhidos(lista);
  }

  function escolher(e: ChangeEvent<HTMLInputElement>) {
    aceitar(Array.from(e.target.files ?? []));
    // Zera o campo: escolher de novo os mesmos arquivos precisa disparar o
    // `onChange` outra vez.
    e.target.value = '';
  }

  function soltar(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setArrastando(false);
    const lista = Array.from(e.dataTransfer.files ?? []);
    if (lista.length > 0) aceitar(lista);
  }

  async function enviar() {
    if (escolhidos.length === 0) return;
    setErro(null);
    setEnviando(true);
    try {
      const forma = new FormData();
      for (const f of escolhidos) forma.append('arquivos', f);
      const { id } = await api<{ id: string }>('/sped/analises', {
        metodo: 'POST',
        arquivo: forma,
      });
      navegar(`/painel/sped/${id}`);
    } catch (err) {
      setErro(
        err instanceof ErroApi
          ? err.message
          : 'Não foi possível enviar os arquivos.',
      );
    } finally {
      setEnviando(false);
    }
  }

  async function remover(a: AnaliseSped) {
    const ok = await confirmar({
      titulo: 'Apagar esta análise?',
      mensagem: `A análise "${a.nome}" e as chaves extraídas dela são apagadas. Buscas já iniciadas a partir dela não são afetadas.`,
      confirmar: 'Apagar análise',
      cancelar: 'Voltar',
      perigo: true,
    });
    if (!ok) return;
    try {
      await api<void>(`/sped/analises/${a.id}`, { metodo: 'DELETE' });
      notificar('Análise apagada.');
      await carregar();
    } catch (err) {
      notificar(
        err instanceof ErroApi ? err.message : 'Não foi possível apagar.',
        'erro',
      );
    }
  }

  const total = escolhidos.reduce((s, f) => s + f.size, 0);

  return (
    <Pagina>
      <TituloPagina
        titulo="SPED"
        descricao="Envie os arquivos e nós extraímos as chaves das notas. Depois é só filtrar o que interessa e baixar os XMLs."
      />

      {erro && <Aviso>{erro}</Aviso>}

      {/* ── Envio ─────────────────────────────────────────────────────── */}
      <Cartao
        titulo="Enviar arquivos"
        descricao="EFD ICMS/IPI e EFD Contribuições, em .txt ou num .zip com tudo dentro."
      >
        <input
          ref={entrada}
          type="file"
          multiple
          accept=".txt,.sped,.zip,text/plain,application/zip"
          onChange={escolher}
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
        />

        <div
          className={cx(css.zona, arrastando && css.zonaAtiva)}
          onDragOver={(e) => {
            e.preventDefault();
            setArrastando(true);
          }}
          onDragLeave={() => setArrastando(false)}
          onDrop={soltar}
        >
          {escolhidos.length === 0 ? (
            <>
              <span className={css.zonaIcone} aria-hidden="true">
                <Upload />
              </span>
              <p className={css.zonaTitulo}>
                Arraste os arquivos para cá ou escolha no computador
              </p>
              <p className={css.zonaTexto}>
                Selecione a pasta do ano inteiro de uma vez — EFD ICMS/IPI e EFD
                Contribuições. Ou mande um <strong>.zip</strong> com tudo
                dentro: ele é expandido aqui e os .txt de qualquer subpasta
                entram na análise. As notas repetidas entre arquivos são
                unificadas.
              </p>
              <Botao
                aparencia="secundario"
                pequeno
                onClick={() => entrada.current?.click()}
              >
                Escolher arquivos
              </Botao>
            </>
          ) : (
            <>
              <p className={css.zonaTitulo}>
                {plural(escolhidos.length, 'arquivo')} · {megabytes(total)}
              </p>
              <ul className={css.escolhidos}>
                {escolhidos.map((f) => (
                  <li key={f.name} className={css.escolhido}>
                    <FileText aria-hidden="true" />
                    <span className={css.escolhidoNome}>{f.name}</span>
                    <span className={css.escolhidoTamanho}>
                      {megabytes(f.size)}
                    </span>
                  </li>
                ))}
              </ul>
              <div className={css.zonaAcoes}>
                <Botao
                  aparencia="secundario"
                  pequeno
                  onClick={() => entrada.current?.click()}
                  disabled={enviando}
                >
                  Trocar arquivos
                </Botao>
                <Botao
                  pequeno
                  icone={<FileSearch />}
                  onClick={() => void enviar()}
                  carregando={enviando}
                >
                  Analisar {plural(escolhidos.length, 'arquivo')}
                </Botao>
              </div>
            </>
          )}
        </div>

        <p className={css.rodapeEnvio}>
          ECD e ECF são reconhecidos, mas não contêm chaves de NF-e — se vierem
          na pasta, aparecem na lista com o motivo, sem quebrar a análise. Num
          ZIP, o que não for .txt/.sped é simplesmente ignorado.
        </p>
      </Cartao>

      <ComoCobramos contexto="Enviar e analisar o SPED é gratuito." />

      {/* ── Histórico ─────────────────────────────────────────────────── */}
      <TituloSecao>Análises</TituloSecao>

      {analises === null ? (
        <Cartao padding={0}>
          <div className={css.carregando}>
            <Esqueleto altura={20} />
            <Esqueleto altura={20} />
            <Esqueleto altura={20} />
          </div>
        </Cartao>
      ) : analises.length === 0 ? (
        <Cartao padding={0}>
          <Vazio
            icone={<FileSearch />}
            titulo="Nenhuma análise ainda"
            descricao="Envie seus arquivos SPED para extrair as chaves e escolher o que baixar."
          />
        </Cartao>
      ) : (
        <Cartao padding={0}>
          <Tabela rotulo="Análises de SPED">
            <thead>
              <tr>
                <Th>Análise</Th>
                <Th>Situação</Th>
                <Th alinhar="direita">Arquivos</Th>
                <Th alinhar="direita">Notas com chave</Th>
                <Th encolher>
                  <span className="sr-only">Ações</span>
                </Th>
              </tr>
            </thead>
            <tbody>
              {analises.map((a) => {
                const status = STATUS_ANALISE[a.status] ?? {
                  texto: a.status,
                  tom: 'info' as const,
                };
                // Avisos por arquivo: é aqui que o ECD/TXT trocado aparece.
                const comAviso = a.arquivosDetalhe.filter((f) => f.aviso);
                const temDetalhe = !!a.erro || comAviso.length > 0;
                return (
                  <Fragment key={a.id}>
                    <tr className={cx(temDetalhe && css.linhaComDetalhe)}>
                      <Td className={css.colAnalise}>
                        <Celula
                          para={`/painel/sped/${a.id}`}
                          principal={a.nome}
                          secundaria={dataHora(a.createdAt)}
                        />
                      </Td>
                      <Td>
                        <Etiqueta tom={status.tom} pulsando={status.pulsando}>
                          {status.texto}
                        </Etiqueta>
                      </Td>
                      <Td alinhar="direita" className="num">
                        {numero(a.arquivos)}
                      </Td>
                      <Td alinhar="direita" className={cx('num', css.notas)}>
                        {numero(a.totalNotas)}
                      </Td>
                      <Td>
                        <div className={css.acoes}>
                          {a.totalNotas > 0 && (
                            <BotaoLink
                              to={`/painel/sped/${a.id}`}
                              aparencia="secundario"
                              pequeno
                              icone={<Filter />}
                            >
                              Filtrar e baixar
                            </BotaoLink>
                          )}
                          <Menu
                            gatilho={({ alternar, ...aria }) => (
                              <Botao
                                aparencia="discreto"
                                pequeno
                                soIcone
                                icone={<MoreHorizontal />}
                                aria-label="Mais ações"
                                onClick={alternar}
                                {...aria}
                              />
                            )}
                          >
                            <ItemMenu
                              icone={<Trash2 />}
                              perigo
                              onClick={() => void remover(a)}
                            >
                              Apagar análise
                            </ItemMenu>
                          </Menu>
                        </div>
                      </Td>
                    </tr>
                    {temDetalhe && (
                      <tr className={css.linhaDetalhe}>
                        <td colSpan={5}>
                          <ul className={css.avisos}>
                            {a.erro && (
                              <li className={css.avisoErro}>
                                <AlertTriangle aria-hidden="true" />
                                <span>{a.erro}</span>
                              </li>
                            )}
                            {comAviso.map((f) => (
                              <li key={f.id}>
                                <AlertTriangle aria-hidden="true" />
                                <span>
                                  <strong>{f.nomeArquivo}</strong> (
                                  {TIPO_SPED[f.tipo] ?? f.tipo}): {f.aviso}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </Tabela>
        </Cartao>
      )}
    </Pagina>
  );
}
